'use client';

import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/routing';
import { useListingNavigation } from '@/shared/hooks/use-listing-navigation';
import { X } from 'lucide-react';
import type { ActiveFilter } from '@/features/games/internal/types/category-filters';
import {
  clearAllFilters,
  removeFilterFromParams,
} from '@/features/games/internal/helpers/filter-url-helpers';
import { useTranslations } from 'next-intl';

type ActiveFiltersProps = {
  filters: ActiveFilter[];
};

export const ActiveFilters = ({ filters }: ActiveFiltersProps) => {
  const t = useTranslations('Categories');
  const router = useRouter();
  const { buildListingHref } = useListingNavigation();
  const searchParams = useSearchParams();

  const handleRemoveFilter = (filter: ActiveFilter) => {
    const currentParams = Object.fromEntries(searchParams?.entries() ?? []);
    const newParams = removeFilterFromParams(currentParams, filter.type, filter.value);
    const newUrl = buildListingHref(newParams);
    router.replace(newUrl, { scroll: false });
  };

  const handleClearAll = () => {
    const currentParams = Object.fromEntries(searchParams?.entries() ?? []);
    const newParams = clearAllFilters(currentParams);
    const newUrl = buildListingHref(newParams);
    router.replace(newUrl, { scroll: false });
  };

  if (filters.length === 0) {
    return (
      <div className='border-border-hover mb-6 border-b pb-4'>
        <h3 className='font-heading text-text mb-3 text-xl uppercase'>{t('refineBy')}</h3>
        <p className='text-text-muted'>{t('noFiltersApplied')}</p>
      </div>
    );
  }

  return (
    <div className='border-border-hover mb-6 border-b pb-4'>
      <div className='mb-3 flex items-center justify-between'>
        <h3 className='font-heading text-text text-xl uppercase'>{t('refineBy')}</h3>
        <button
          onClick={handleClearAll}
          className='tracking-wide uppercase underline hover:no-underline'
        >
          {t('clearAll')}
        </button>
      </div>
      <div className='flex flex-wrap gap-2'>
        {filters.map((filter) => (
          <button
            key={`${filter.type}-${filter.value}`}
            onClick={() => handleRemoveFilter(filter)}
            className='bg-background-subtle group hover:bg-border-hover flex items-center gap-2 rounded-sm px-3 py-1.5 transition-colors'
          >
            <span className='text-text'>{filter.label}</span>
            <X className='text-text-muted group-hover:text-text h-4 w-4' />
          </button>
        ))}
      </div>
    </div>
  );
};
